/**
 * Audit-log page body.
 *
 * Mirrors `prototype/admin/audit-log.html`: header, filter strip, KPI strip,
 * reverse-chrono grouped events, right-side actor/object/sign-in panels.
 */
import { type ReactNode, useMemo, useState } from "react";
import type { auditLog as auditContract } from "@flint/api-contract";
import {
  Bot,
  CalendarClock,
  Download,
  Filter,
  KeyRound,
  MessageCircle,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { Alert, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import {
  deriveInitials,
  formatDate,
  formatDateTime,
  formatRelativeTime,
} from "../../lib/format.ts";

type AuditRow = auditContract.AuditRow;
type AuditCategory = auditContract.AuditCategory;
type AuditActorType = auditContract.AuditActorType;

interface Filters {
  query: string;
  category: AuditCategory | undefined;
  actorType: AuditActorType | undefined;
  entityType: string | undefined;
  verb: string | undefined;
}

const CATEGORIES: Array<{ value: AuditCategory; label: string }> = [
  { value: "records", label: "Records" },
  { value: "money", label: "Money" },
  { value: "compliance", label: "Compliance" },
  { value: "settings", label: "Settings" },
  { value: "auth", label: "Auth" },
  { value: "integrations", label: "Integration" },
  { value: "ai", label: "AI" },
];

const auditLogTrpc = trpc as unknown as {
  auditLog: {
    list: {
      useQuery(input: auditContract.ListInput): {
        data: auditContract.ListOutput | undefined;
        isLoading: boolean;
        error: { message: string } | null;
      };
    };
  };
};

/** Public route component for `/records/audit-log`. */
export function AuditLogPage() {
  const [filters, setFilters] = useState<Filters>({
    query: "",
    category: undefined,
    actorType: undefined,
    entityType: undefined,
    verb: undefined,
  });
  const [selectedEntry, setSelectedEntry] = useState<AuditRow | null>(null);

  const list = auditLogTrpc.auditLog.list.useQuery({
    limit: 50,
    ...(filters.query.trim() ? { query: filters.query.trim() } : {}),
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.actorType ? { actorType: filters.actorType } : {}),
    ...(filters.entityType ? { entityType: filters.entityType } : {}),
    ...(filters.verb ? { verb: filters.verb } : {}),
  });

  const data = list.data;
  const groups = useMemo(() => groupByDay(data?.items ?? []), [data?.items]);
  const activeFilterCount = countActiveFilters(filters);

  return (
    <main className="audit-log-page">
      <header className="page-head">
        <div className="page-head-text">
          <h1>Audit log</h1>
          <p className="lead">
            <span>Append-only trail</span>
            <span className="dot" aria-hidden />
            <span>Every write and sensitive read</span>
            {data ? (
              <>
                <span className="dot" aria-hidden />
                <span>
                  <strong>{data.kpis.total30d}</strong> events in 30 days
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className="btn-secondary"
            type="button"
            disabled={!data || data.items.length === 0}
            onClick={() => (data ? exportAuditCsv(data.items) : undefined)}
            title={
              data && data.items.length > 0
                ? "Export the currently loaded audit rows"
                : "No audit rows to export"
            }
          >
            <Download size={13} strokeWidth={2} />
            Export CSV
          </button>
          <button
            className="btn-primary"
            type="button"
            disabled
            title="Event stream webhooks land in V1.x"
          >
            Stream events
          </button>
        </div>
      </header>

      <section className="filter-row" aria-label="Audit log filters">
        <label className="filter-search">
          <Search size={14} strokeWidth={2} />
          <input
            value={filters.query}
            onChange={(event) => setFilters((prev) => ({ ...prev, query: event.target.value }))}
            placeholder="Search actor, entity, ID or metadata..."
          />
          <span className="kbd">/</span>
        </label>
        <div className="filter-controls" aria-label="Filter controls">
          <Select
            value={filters.actorType ?? "all"}
            onValueChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                actorType: value === "all" ? undefined : (value as AuditActorType),
              }));
            }}
          >
            <SelectTrigger className="select-chip" aria-label="Actor type">
              <SelectValue placeholder="Any actor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any actor</SelectItem>
              <SelectItem value="web">Web users</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="api">API</SelectItem>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="ai">Flint AI</SelectItem>
            </SelectContent>
          </Select>
          {data ? (
            <>
              <Select
                value={filters.entityType ?? "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    entityType: value === "all" ? undefined : value,
                  }));
                }}
              >
                <SelectTrigger className="select-chip" aria-label="Entity type">
                  <SelectValue placeholder="All objects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All objects</SelectItem>
                  {data.availableEntityTypes.map((entityType) => (
                    <SelectItem key={entityType} value={entityType}>
                      {labelize(entityType)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.verb ?? "all"}
                onValueChange={(value) => {
                  setFilters((prev) => ({
                    ...prev,
                    verb: value === "all" ? undefined : value,
                  }));
                }}
              >
                <SelectTrigger className="select-chip" aria-label="Event verb">
                  <SelectValue placeholder="All events" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {data.availableVerbs.map((verb) => (
                    <SelectItem key={verb} value={verb}>
                      {labelize(verb)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : null}
          {activeFilterCount > 0 ? (
            <button
              className="clear-all"
              type="button"
              onClick={() => {
                setFilters({
                  query: "",
                  category: undefined,
                  actorType: undefined,
                  entityType: undefined,
                  verb: undefined,
                });
              }}
            >
              <XCircle size={13} strokeWidth={2.2} />
              Clear {activeFilterCount}
            </button>
          ) : null}
        </div>
        {CATEGORIES.map((category) => (
          <button
            key={category.value}
            type="button"
            className={`chip ${filters.category === category.value ? "is-on" : ""}`}
            onClick={() => {
              setFilters((prev) => ({
                ...prev,
                category: prev.category === category.value ? undefined : category.value,
              }));
            }}
          >
            <Filter size={12} strokeWidth={2} />
            {category.label}
          </button>
        ))}
        <span className="filter-label">
          <SlidersHorizontal size={13} strokeWidth={2.2} />
          Filters
        </span>
      </section>

      {list.isLoading ? (
        <p className="loading-line">Loading audit log...</p>
      ) : list.error ? (
        <Alert intent="bad" title="Couldn't load audit log" description={list.error.message} />
      ) : data ? (
        <>
          <KpiStrip data={data} />
          <div className="work-frame">
            <section className="events-col" aria-label="Audit events">
              {groups.length > 0 ? (
                groups.map((group) => (
                  <div className="day-group" key={group.key}>
                    <div className="day-head">
                      <span className="label">
                        <strong>{group.label}</strong>
                      </span>
                      <span className="count">{group.items.length} events</span>
                      <span className="line" aria-hidden />
                    </div>
                    <div className="events">
                      {group.items.map((entry) => (
                        <AuditEventRow key={entry.id} entry={entry} onInspect={setSelectedEntry} />
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <ShieldCheck size={20} strokeWidth={2} />
                  <strong>No events match these filters</strong>
                  <span>Clear filters or search for another actor, object, or event.</span>
                </div>
              )}
              <div className="pagination">
                Showing <strong>{data.items.length}</strong> events
                {data.nextCursor ? (
                  <button
                    className="btn-mini"
                    type="button"
                    disabled
                    title="Cursor load-more lands in V1.0.x"
                  >
                    Load more
                  </button>
                ) : null}
                <span aria-hidden>·</span>
                <button
                  className="btn-mini"
                  type="button"
                  disabled
                  title="Jump-to-date lands in V1.x"
                >
                  Jump to date...
                </button>
              </div>
            </section>
            <AuditAside data={data} />
          </div>
          {selectedEntry ? (
            <AuditDetailPanel entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
          ) : null}
        </>
      ) : null}
    </main>
  );
}

function KpiStrip({ data }: { data: auditContract.ListOutput }) {
  return (
    <section className="kpis" aria-label="Audit log KPIs">
      <KpiCard
        tone="indigo"
        label="30-day events"
        value={data.kpis.total30d}
        meta="Reverse chronological"
      />
      <KpiCard tone="emerald" label="Today" value={data.kpis.today} meta="Current tenant only" />
      <KpiCard
        tone="amber"
        label="Sensitive reads"
        value={data.kpis.sensitiveReads30d}
        meta="Unmask / read signals"
      />
      <KpiCard
        tone="violet"
        label="Integration events"
        value={data.kpis.integration30d}
        meta="API, webhook, sends"
      />
    </section>
  );
}

function KpiCard({
  tone,
  label,
  value,
  meta,
}: {
  tone: "indigo" | "emerald" | "amber" | "violet";
  label: string;
  value: number;
  meta: string;
}) {
  return (
    <article className={`kpi s-${tone}`}>
      <span className="kpi-stripe" aria-hidden />
      <div className="kpi-head">
        <span>{label}</span>
        <span className="ic">
          <CalendarClock size={14} strokeWidth={2} />
        </span>
      </div>
      <div className="kpi-val">{value}</div>
      <div className="kpi-meta">{meta}</div>
    </article>
  );
}

function AuditEventRow({
  entry,
  onInspect,
}: {
  entry: AuditRow;
  onInspect: (entry: AuditRow) => void;
}) {
  const actor = actorLabel(entry);
  const target = targetLabel(entry);
  return (
    <article className={`ev ${severityClass(entry.severity)}`}>
      <div className="ev-time">
        {timeOnly(entry.at)}
        <small>{formatRelativeTime(entry.at)}</small>
      </div>
      <div className={`ev-actor ${actorClass(entry)}`} aria-hidden>
        {actorAvatar(entry)}
      </div>
      <div className="ev-body">
        <div className="line">
          <span className="who">{actor}</span>
          <span className={`verb ${verbClass(entry.verb)}`}>{labelize(entry.verb)}</span>
          <span>
            {labelize(entry.entityType)} <span className="target-doc">{target}</span>
          </span>
        </div>
        <div className="meta">
          {metadataChips(entry).map((chip) => (
            <span key={chip}>{chip}</span>
          ))}
          {entry.beforeHash || entry.afterHash ? (
            <span>
              Hash {shortHash(entry.beforeHash)} {"->"} {shortHash(entry.afterHash)}
            </span>
          ) : null}
          <span>ID {shortId(entry.id)}</span>
        </div>
      </div>
      <div className="ev-type">
        <span className={`pill ${entry.category}`}>{categoryLabel(entry.category)}</span>
      </div>
      <button
        className="ev-focus"
        type="button"
        aria-label={`Inspect ${target}`}
        onClick={() => onInspect(entry)}
      >
        <Search size={14} strokeWidth={2} />
      </button>
    </article>
  );
}

function AuditDetailPanel({ entry, onClose }: { entry: AuditRow; onClose: () => void }) {
  const target = targetLabel(entry);
  const chips = metadataChips(entry);
  const metadataRows = Object.entries(entry.metadata)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="detail-overlay" role="presentation" onMouseDown={onClose}>
      <aside
        className="detail-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Audit details for ${target}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="detail-head">
          <div className={`detail-hero ${severityClass(entry.severity)}`}>
            <div className={`detail-avatar ${actorClass(entry)}`} aria-hidden>
              {actorAvatar(entry)}
            </div>
            <div className="detail-title">
              <span className={`pill ${entry.category}`}>{categoryLabel(entry.category)}</span>
              <h2>
                {labelize(entry.verb)} {labelize(entry.entityType)}
              </h2>
              <p>{target}</p>
            </div>
          </div>
          <button
            className="detail-close"
            type="button"
            aria-label="Close details"
            onClick={onClose}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <section className="detail-summary">
          <div className="summary-card actor-card">
            <span className="summary-label">Actor</span>
            <div className="summary-person">
              <div className={`ava ${entry.actorType}`} aria-hidden>
                {actorAvatar(entry)}
              </div>
              <div>
                <strong>{actorLabel(entry)}</strong>
                <small>
                  {actorTypeLabel(entry.actorType)}
                  {entry.actorRole ? ` · ${entry.actorRole}` : ""}
                </small>
              </div>
            </div>
          </div>
          <div className="summary-card object-card">
            <span className="summary-label">Object</span>
            <strong>{labelize(entry.entityType)}</strong>
            <small>{target}</small>
          </div>
          <div className="summary-card">
            <span className="summary-label">Event time</span>
            <strong>{formatDateTime(entry.at)}</strong>
            <small>{formatRelativeTime(entry.at)}</small>
          </div>
          <div className="summary-card">
            <span className="summary-label">Severity</span>
            <strong>{labelize(entry.severity)}</strong>
            <small>{categoryLabel(entry.category)}</small>
          </div>
        </section>

        {chips.length > 0 ? (
          <section className="detail-chips" aria-label="Audit highlights">
            {chips.map((chip) => (
              <span key={chip}>{chip}</span>
            ))}
          </section>
        ) : null}

        <section className="detail-timeline" aria-label="Event timeline">
          <div>
            <span className="mark" />
            <strong>Audit row created</strong>
            <small>{formatDateTime(entry.at)}</small>
          </div>
          <div>
            <span className="mark" />
            <strong>{labelize(entry.verb)} applied</strong>
            <small>
              {labelize(entry.entityType)} · {target}
            </small>
          </div>
        </section>

        <div className="detail-grid">
          <DetailField label="Event ID" value={entry.id} wide />
          <DetailField label="Object ID" value={entry.entityId} wide />
          <DetailField label="Before hash" value={entry.beforeHash ?? "None"} wide />
          <DetailField label="After hash" value={entry.afterHash ?? "None"} wide />
        </div>

        <section className="detail-section">
          <h3>What Changed</h3>
          {metadataRows.length > 0 ? (
            <dl className="metadata-list">
              {metadataRows.map(([key, value]) => (
                <div key={key}>
                  <dt>{labelize(key)}</dt>
                  <dd>{metadataValue(value)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="detail-empty">No extra metadata was recorded for this event.</p>
          )}
        </section>

        <section className="detail-section">
          <h3>Raw Metadata</h3>
          <pre>{JSON.stringify(entry.metadata, null, 2)}</pre>
        </section>
      </aside>
    </div>
  );
}

function DetailField({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string;
  wide?: boolean;
}) {
  return (
    <div className={`detail-field ${wide ? "wide" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AuditAside({ data }: { data: auditContract.ListOutput }) {
  const maxActor = Math.max(...data.topActors.map((row) => row.count), 1);
  return (
    <aside className="work-aside">
      <section className="panel">
        <div className="panel-head">
          <h3>
            <UserRound size={14} strokeWidth={2} />
            Top actors · 30 days
          </h3>
          <span className="right">{data.topActors.length} actors</span>
        </div>
        <div className="panel-body">
          {data.topActors.map((actor) => (
            <div className="actor-row" key={`${actor.actorType}:${actor.actorId}`}>
              <div className={`ava ${actor.actorType}`} aria-hidden>
                {actorAvatar(actor)}
              </div>
              <div>
                <strong>{actor.actorName ?? actorTypeLabel(actor.actorType)}</strong>
                <small>{actor.actorRole ?? actorTypeLabel(actor.actorType)}</small>
              </div>
              <span className="ct">{actor.count}</span>
              <div className="bar">
                <div style={{ width: `${Math.round((actor.count / maxActor) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>
            <SlidersHorizontal size={14} strokeWidth={2} />
            Most-changed objects
          </h3>
          <span className="right">30 days</span>
        </div>
        <div className="panel-body">
          {data.topObjects.map((object) => (
            <div className="obj-row" key={object.entityType}>
              <span className="ic" aria-hidden>
                <KeyRound size={11} strokeWidth={2} />
              </span>
              <span className="name">
                <strong>{labelize(object.entityType)}</strong>
              </span>
              <span className="ct">{object.count}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>
            <ShieldCheck size={14} strokeWidth={2} />
            Recent sign-ins
          </h3>
          <span className="right">Last 7 days</span>
        </div>
        <div className="panel-body">
          {data.recentSignIns.length > 0 ? (
            data.recentSignIns.map((entry) => (
              <div className={`signin ${entry.severity === "rose" ? "failed" : ""}`} key={entry.id}>
                <span className="ic" aria-hidden>
                  <ShieldCheck size={12} strokeWidth={2.4} />
                </span>
                <div className="body">
                  <strong>{actorLabel(entry)}</strong>
                  <small>
                    {labelize(entry.verb)}
                  </small>
                </div>
                <span className="t">{formatDateTime(entry.at)}</span>
              </div>
            ))
          ) : (
            <p className="panel-empty">No sign-in audit rows in the last 7 days.</p>
          )}
        </div>
      </section>

      <div className="retention">
        <strong>Retention 8 years</strong> · Flint keeps audit rows for Indian tax and compliance
        review.
        <br />
        <span>Hash-chained tamper evidence lands in V1.x.</span>
      </div>
    </aside>
  );
}

function groupByDay(items: AuditRow[]) {
  const map = new Map<string, AuditRow[]>();
  for (const item of items) {
    const key = new Date(item.at).toISOString().slice(0, 10);
    map.set(key, [...(map.get(key) ?? []), item]);
  }
  return [...map.entries()].map(([key, rows]) => ({
    key,
    label: formatDate(`${key}T00:00:00.000Z`),
    items: rows,
  }));
}

function metadataChips(entry: AuditRow): string[] {
  const fields = [
    "summary",
    "reason",
    "status",
    "mode",
    "reference",
    "channel",
    "ip",
    "city",
    "source",
  ];
  const chips = fields
    .map((field) => {
      const value = entry.metadata[field];
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        // Special-case the channel chip so WhatsApp-decided rows read
        // naturally ("via WhatsApp") instead of the auto-labeled
        // "Channel whatsapp". Same for web fallback.
        if (field === "channel") {
          if (value === "whatsapp") return "via WhatsApp";
          if (value === "web") return "via web";
        }
        return `${labelize(field)} ${String(value)}`;
      }
      return null;
    })
    .filter((value): value is string => value !== null);
  return chips.slice(0, 4);
}

function actorLabel(entry: Pick<AuditRow, "actorType" | "actorId" | "actorName">): string {
  if (entry.actorName) return entry.actorName;
  if (entry.actorType === "ai") return "Flint AI";
  if (entry.actorType === "system") return "System";
  if (entry.actorType === "api") return "API client";
  if (entry.actorType === "whatsapp") return "WhatsApp";
  return "Web user";
}

function actorAvatar(entry: Pick<AuditRow, "actorType" | "actorName" | "actorId">): ReactNode {
  if (entry.actorType === "ai") return <Bot size={13} strokeWidth={2.4} />;
  if (entry.actorType === "system" || entry.actorType === "api")
    return <ShieldCheck size={12} strokeWidth={2.4} />;
  if (entry.actorType === "whatsapp") return <MessageCircle size={12} strokeWidth={2.4} />;
  return deriveInitials(entry.actorName ?? actorTypeLabel(entry.actorType));
}

function actorClass(entry: Pick<AuditRow, "actorType" | "actorName">): string {
  if (entry.actorType === "ai") return "b-flint";
  if (entry.actorType === "system" || entry.actorType === "api") return "b-system";
  if (entry.actorType === "whatsapp") return "b-whatsapp";
  if (entry.actorName?.toLowerCase().includes("priya")) return "b-priya";
  return "b-rakesh";
}

function targetLabel(entry: AuditRow): string {
  const keys = [
    "docNumber",
    "documentNumber",
    "invoiceNumber",
    "vendorDocNumber",
    "reference",
    "name",
    "vendorName",
    "customerName",
  ];
  for (const key of keys) {
    const value = entry.metadata[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  // No semantic name in the metadata. Fall back to a short id; the
  // entity-type label is already printed by the caller, so returning
  // "Bill" here would stutter as "Bill Bill" on the rendered line.
  if (entry.entityType === "invoice" && entry.verb.includes("draft")) return "Draft invoice";
  if (entry.entityType === "vendor_payment") return "Vendor payment";
  if (entry.entityType === "receipt") return "Receipt";
  return shortId(entry.entityId);
}

function metadataValue(value: unknown): string {
  if (Array.isArray(value) || (value && typeof value === "object")) {
    return JSON.stringify(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function exportAuditCsv(items: AuditRow[]): void {
  const rows = [
    [
      "Time",
      "Actor",
      "Actor type",
      "Category",
      "Entity type",
      "Entity ID",
      "Verb",
      "Target",
      "Metadata",
    ],
    ...items.map((entry) => [
      entry.at,
      actorLabel(entry),
      actorTypeLabel(entry.actorType),
      categoryLabel(entry.category),
      labelize(entry.entityType),
      entry.entityId,
      labelize(entry.verb),
      targetLabel(entry),
      JSON.stringify(entry.metadata),
    ]),
  ];
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `flint-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function categoryLabel(category: AuditCategory): string {
  return category === "integrations" ? "Integration" : labelize(category);
}

function actorTypeLabel(type: AuditActorType): string {
  switch (type) {
    case "ai":
      return "Flint AI";
    case "api":
      return "API";
    case "system":
      return "System";
    case "whatsapp":
      return "WhatsApp";
    case "web":
      return "Web user";
    case "buyer":
      return "Storefront buyer";
  }
}

function labelize(value: string): string {
  return value.replace(/[_-]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function timeOnly(value: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function shortId(value: string): string {
  if (value.length <= 10) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function shortHash(value: string | null): string {
  return value ? value.slice(0, 8) : "none";
}

function verbClass(verb: string): string {
  if (
    ["created", "issued", "paid", "recorded", "approved", "granted"].some((part) =>
      verb.includes(part),
    )
  )
    return "created";
  if (["removed", "deleted", "voided", "rejected", "failed"].some((part) => verb.includes(part)))
    return "deleted";
  return "updated";
}

function severityClass(severity: auditContract.AuditSeverity): string {
  if (severity === "good") return "s-good";
  if (severity === "warn") return "s-warn";
  if (severity === "rose") return "s-rose";
  if (severity === "info") return "s-info";
  if (severity === "violet") return "s-violet";
  return "";
}

function countActiveFilters(filters: Filters): number {
  return [
    filters.query.trim(),
    filters.category,
    filters.actorType,
    filters.entityType,
    filters.verb,
  ].filter(Boolean).length;
}
